import { ATTR, MODE_FAST, QR_REFRESH_MS } from "../constants";
import { asBufferSource } from "../crypto";
import { decodeAttributes, decodeCredential, decodeErrand } from "../format";
import { ICON } from "../icons";
import { buildPresentation, chooseNonce } from "../present";
import { shrinkPicture } from "../picture";
import { emitQR } from "../qr";
import {
  correctedNow, liveErrands, loadCredential, loadCredentialLite, loadDeviceKey,
} from "../storage";
import { credentialPhase, currentClockAlarms, expiresAtOf, uploadPortrait } from "../sync";
import * as nav from "../nav";
import type { Layer } from "../nav";
import { alarmsHTML, escapeHTML, formatWhen, swipeToClose } from "../ui";
import { runningBuild } from "../update";

/**
 * How long the credential has left, in whole units rounded down: a card that
 * says three days when two and a half remain is a promise the credential does
 * not keep.
 */
export function validityCopy(secondsLeft: number): string {
  const days = Math.floor(secondsLeft / 86_400);
  if (days >= 1) return `Válida ${days} ${days === 1 ? "día" : "días"} más`;
  const hours = Math.floor(secondsLeft / 3600);
  if (hours >= 1) return `Válida ${hours} ${hours === 1 ? "hora" : "horas"} más`;
  return "Válida menos de una hora más";
}

/** Where the card's buttons lead; `main.ts` owns the navigation. The
 *  verifier and the history are `data-view` buttons that `main.ts` catches. */
export interface CardActions {
  createErrand: () => void;
  receiveErrand: () => void;
  /** With the errand chosen on the card, so a strong-mode answer carries it
   *  the way the fast-mode code does. */
  answerChallenge: (errandBytes?: Uint8Array) => void;
  /** After the holder confirmed; the card only asks. */
  logout: () => void;
  /** Draw the card again, from the credential now stored. */
  refresh: () => void;
}

/** What the card says when a new photograph did not go up. */
const PHOTO_COPY = {
  offline: "Necesitas conexión para cambiar tu foto.",
  unreadable: "No se pudo leer esa imagen. Prueba con otra.",
  refused: "La UH no aceptó la foto ahora. Inténtalo más tarde.",
} as const;

/** An errand this holder keeps, as the card lists it. */
interface HeldErrand {
  bytes: Uint8Array;
  grantorName: string;
  text: string;
  expiresAt: number;
}

/** Stored errands were verified when they were received, so this only reads
 *  them; one that no longer decodes is left off the list rather than shown. */
function held(bytes: Uint8Array): HeldErrand | null {
  try {
    const errand = decodeErrand(bytes);
    const grantor = decodeAttributes(decodeCredential(errand.credentialBytes).attributesBytes);
    return {
      bytes, grantorName: String(grantor.get(ATTR.NAME) ?? ""), text: errand.text,
      expiresAt: errand.expiresAt,
    };
  } catch {
    return null;
  }
}

/**
 * The holder's own screen. The code is not on it: it opens over the card when
 * the holder taps «Mostrar», and only then is a presentation signed. A code
 * nobody asked to show is a code someone can photograph off a phone left on a
 * table, and it costs a signature every ten seconds for nothing.
 */
export async function renderCard(root: HTMLElement, actions?: CardActions): Promise<() => void> {
  const credentialBytes = await loadCredential();
  const pair = await loadDeviceKey();
  if (!credentialBytes || !pair) throw new Error("no credential on this device");
  // No lite credential, no errand to make: a wallet from before VS3 gets one
  // at its next renewal, and until then the button is not offered.
  const canCreate = (await loadCredentialLite()) !== null;
  const errands = (await liveErrands((await correctedNow()).now))
    .map(held).filter((errand): errand is HeldErrand => errand !== null);

  const attributes = decodeAttributes(decodeCredential(credentialBytes).attributesBytes);
  const expiresAt = expiresAtOf(credentialBytes);
  const portrait = attributes.get(ATTR.PORTRAIT) as Uint8Array | undefined;
  const portraitURL = portrait
    ? URL.createObjectURL(new Blob([asBufferSource(portrait)], { type: "image/webp" }))
    : null;
  const name = escapeHTML(attributes.get(ATTR.NAME));

  root.innerHTML = `
    <article class="card">
      <div class="uhb">Universidad de La Habana</div>
      <button id="forward" class="icon corner left" aria-label="Adelante" hidden>${ICON.forward}</button>
      <button id="menu-open" class="icon corner" aria-label="Más opciones">${ICON.more}</button>
      ${portraitURL
        ? `<img class="portrait" src="${portraitURL}" alt="">`
        : `<div class="portrait none">${ICON.person}</div>`}
      <h1>${name}</h1>
      <p class="role">${escapeHTML(attributes.get(ATTR.ROLE))}</p>
      <p class="dep">${escapeHTML(attributes.get(ATTR.DEPARTMENT))} · ${escapeHTML(attributes.get(ATTR.FACULTY))}</p>
      <div id="alarms"></div>
      <p id="photo-note" class="warn" aria-live="polite"></p>
      <p id="code-problem" class="warn"></p>
      <button id="show-code" class="orb">${ICON.qr}Mostrar</button>
      <p id="validity" class="valid"></p>
      <div class="pills">
        <button class="pill" data-view="scan">${ICON.scan}Verificar</button>
        <button class="pill" id="errands-open">${ICON.errand}Recados</button>
        <button class="pill square" data-view="history" aria-label="Historial">${ICON.history}</button>
      </div>
    </article>
    <section id="code" aria-label="Tu código" hidden>
      <div class="spacer"></div>
      <div id="errands" class="chips" aria-label="Recados"></div>
      <div id="qr" class="qr"></div>
      <div class="who">
        ${portraitURL
          ? `<img class="mini" src="${portraitURL}" alt="">`
          : `<div class="mini none">${ICON.person}</div>`}
        <div><b>${name}</b><span id="age" aria-live="polite"></span></div>
      </div>
      <button id="challenge-answer" class="link">¿Te piden modo reforzado? Responder a un desafío</button>
      <button id="code-close" class="icon close" aria-label="Ocultar el código">${ICON.close}</button>
    </section>
    <div id="errands-sheet" class="sheet" hidden><div class="panel">
      <button class="icon sheet-close" aria-label="Cerrar">${ICON.close}</button>
      <h2>Recados</h2>
      ${canCreate ? `<button id="errand-create" class="row">${ICON.plus}<span>Crear recado
        <small>Autoriza a alguien a hacer algo en tu nombre</small></span></button>` : ""}
      <button id="errand-receive" class="row">${ICON.receive}<span>Recibir recado
        <small>Guarda uno que alguien firmó para ti</small></span></button>
      <div id="held"></div>
    </div></div>
    <div id="menu" class="sheet" hidden><div class="panel">
      <button class="icon sheet-close" aria-label="Cerrar">${ICON.close}</button>
      <h2>Opciones</h2>
      <button class="row" data-view="history">${ICON.history}<span>Historial
        <small>Quién te verificó y a quién verificaste</small></span></button>
      <button id="photo-change" class="row">${ICON.camera}<span>Cambiar mi foto
        <small>Con la cámara o de la galería</small></span></button>
      <input id="photo-input" type="file" accept="image/*" hidden>
      <button id="menu-challenge" class="row">${ICON.shield}<span>Responder a un desafío
        <small>Cuando te verifican en modo reforzado</small></span></button>
      <button id="logout-open" class="row red">${ICON.logout}<span>Cerrar sesión en este dispositivo</span></button>
      <p id="version" class="version"></p>
    </div></div>
    <div id="logout-confirm" class="sheet" hidden><div class="panel">
      <h2>Cerrar sesión</h2>
      <div class="text"><p>Este dispositivo va a perder tu identidad: se borra de aquí, y con
        él ya no podrás mostrar tu código ni verificar a nadie. Tus otros dispositivos
        siguen igual. Para volver a usarlo tendrás que descargar tu identidad de nuevo con
        tu usuario y contraseña.</p></div>
      <div class="actions">
        <button id="logout-yes" class="danger">Sí, cerrar sesión</button>
        <button id="logout-no" class="ghost">Cancelar</button>
      </div>
    </div></div>`;

  const $ = <T extends HTMLElement>(selector: string) => root.querySelector<T>(selector)!;
  const qr = $<HTMLDivElement>("#qr");
  const code = $<HTMLElement>("#code");
  const age = $<HTMLSpanElement>("#age");
  const validity = $<HTMLParagraphElement>("#validity");
  const problem = $<HTMLParagraphElement>("#code-problem");
  const showButton = $<HTMLButtonElement>("#show-code");
  const alarms = $<HTMLDivElement>("#alarms");
  const chips = $<HTMLDivElement>("#errands");
  const heldList = $<HTMLDivElement>("#held");
  const menu = $<HTMLDivElement>("#menu");
  const errandSheet = $<HTMLDivElement>("#errands-sheet");
  const logoutConfirm = $<HTMLDivElement>("#logout-confirm");

  // Every layer is an entry in the history (`nav.ts`): opening one pushes it,
  // and every way of closing one is the same back, so the phone's back
  // gesture, the close button, a tap on the dimmed card and a swipe down all
  // agree. `applyLayer` is the only place a layer is shown or hidden.
  const sheets: Record<Exclude<Layer, "code">, HTMLElement> = {
    menu, errands: errandSheet, logout: logoutConfirm,
  };
  for (const sheet of Object.values(sheets)) {
    sheet.addEventListener("click", (event) => {
      if (event.target === sheet) nav.back();
    });
    swipeToClose(sheet.querySelector<HTMLElement>(".panel")!, nav.back);
  }
  swipeToClose(code, nav.back);
  for (const button of root.querySelectorAll(".sheet-close")) {
    button.addEventListener("click", nav.back);
  }
  // Which release this phone is running, so a holder asked «¿tienes la última?»
  // can answer, and a tester can tell a deploy reached the phone.
  void runningBuild().then((build) => {
    if (build) $("#version").textContent = `Versión ${build.slice(0, 8)}`;
  });
  $("#menu-open").addEventListener("click", () => nav.open("menu"));
  $("#errands-open").addEventListener("click", () => nav.open("errands"));
  const forwardButton = $<HTMLButtonElement>("#forward");
  forwardButton.addEventListener("click", nav.forward);
  $("#errand-receive").addEventListener("click", () => actions?.receiveErrand());
  root.querySelector("#errand-create")?.addEventListener("click", () => actions?.createErrand());
  $("#challenge-answer").addEventListener("click", () => actions?.answerChallenge(selected?.bytes));
  $("#menu-challenge").addEventListener("click", () => actions?.answerChallenge());
  // Two steps, because the second one cannot be undone from this device: the
  // menu entry only opens the explanation.
  $("#logout-open").addEventListener("click", () => nav.swap("logout"));
  $("#logout-no").addEventListener("click", nav.back);
  const photoNote = $<HTMLParagraphElement>("#photo-note");
  const photoInput = $<HTMLInputElement>("#photo-input");
  $("#photo-change").addEventListener("click", () => {
    nav.back();
    photoInput.click();
  });
  photoInput.addEventListener("change", async () => {
    const file = photoInput.files?.[0];
    photoInput.value = "";
    if (!file) return;
    photoNote.textContent = "Subiendo tu foto…";
    let picture: Uint8Array;
    try {
      picture = await shrinkPicture(file);
    } catch {
      photoNote.textContent = PHOTO_COPY.unreadable;
      return;
    }
    const outcome = await uploadPortrait(picture).catch(() => "refused" as const);
    if (outcome === "uploaded") actions?.refresh();
    else photoNote.textContent = PHOTO_COPY[outcome];
  });
  $("#logout-yes").addEventListener("click", () => actions?.logout());
  // The errand riding in key 6 of the QR, or null for the identity alone.
  let selected: HeldErrand | null = null;

  const listErrands = () => {
    const choice = (index: number, label: string, pressed: boolean) =>
      `<button class="errand" data-index="${index}" aria-pressed="${pressed}">${label}</button>`;
    chips.innerHTML = errands.length === 0
      ? ""
      : choice(-1, "Solo identidad", selected === null) + errands.map((errand, index) =>
        choice(index, `Recado de ${escapeHTML(errand.grantorName)}`, selected === errand)).join("");
    heldList.innerHTML = errands.map((errand, index) =>
      `<button class="row held" data-index="${index}">${ICON.errand}<span>De ${escapeHTML(errand.grantorName)}
        <small>${escapeHTML(errand.text)} · vence ${escapeHTML(formatWhen(errand.expiresAt))}</small></span></button>`,
    ).join("");
    for (const button of chips.querySelectorAll<HTMLButtonElement>("button.errand")) {
      button.addEventListener("click", () => {
        const index = Number(button.dataset.index);
        selected = index < 0 ? null : errands[index];
        listErrands();
        void regenerate();
      });
    }
    // An errand picked from the sheet is shown straight away, riding the code.
    for (const button of heldList.querySelectorAll<HTMLButtonElement>("button.held")) {
      button.addEventListener("click", () => {
        selected = errands[Number(button.dataset.index)];
        nav.swap("code");
      });
    }
  };
  listErrands();
  // `null` while there is no code on screen: the code closed, or the unsynced
  // case below. Left at 0 it would make `tickAge` report the age of a QR that
  // was never generated, in seconds since 1970.
  let generatedAt: number | null = null;
  // Keeps the screen on while the code is up, where the browser offers it.
  let wake: WakeLockSentinel | null = null;

  const closeCode = () => {
    code.hidden = true;
    qr.innerHTML = "";
    generatedAt = null;
    age.textContent = "";
    void wake?.release().catch(() => {});
    wake = null;
  };

  const openCode = async () => {
    listErrands();
    code.hidden = false;
    await regenerate();
    try {
      wake = (await navigator.wakeLock?.request("screen")) ?? null;
    } catch {
      /* refused, or no such API: the code works the same, the screen may dim */
    }
  };
  showButton.addEventListener("click", () => nav.open("code"));
  $("#code-close").addEventListener("click", nav.back);

  let layer: Layer | null = null;
  const applyLayer = (next: Layer | null) => {
    forwardButton.hidden = !nav.canGoForward();
    if (next === layer) return;
    for (const [name, sheet] of Object.entries(sheets)) sheet.hidden = name !== next;
    if (layer === "code") closeCode();
    layer = next;
    if (next === "code") void openCode();
  };
  const stopFollowing = nav.onPlace((place) => {
    if (place.view === "card") applyLayer(place.layer);
  });

  const regenerate = async () => {
    // Read on every redraw rather than once: the hourly sync can clear an
    // alarm, and a card left open across a week can earn one.
    alarms.innerHTML = alarmsHTML(await currentClockAlarms());
    const { now, synced } = await correctedNow();
    // A device that never learned the server's time stamps its presentations
    // with its raw clock, and `verify.ts` refuses those with `clock-unsynced`
    // before it looks at anything else. Offering a code anyway puts a
    // normal-looking QR on the holder's screen that every correct verifier
    // refuses, and tells the holder nothing. So the button goes dark and the
    // card says why.
    //
    // `main.ts` sends a credential past the grace period to enrolment before
    // this screen is drawn, so anything not valid here is the expired case,
    // which every verifier refuses too.
    const phase = synced ? credentialPhase(expiresAt, now, null) : null;
    if (!synced || phase?.phase !== "valid") {
      problem.textContent = synced
        ? "Tu identidad caducó. Conéctate a la red para renovarla."
        : `Este teléfono nunca ha puesto en hora la aplicación. Conéctate a internet
          una vez y vuelve a abrirla: mientras tanto, el código que genere no lo
          acepta ningún verificador.`;
      validity.textContent = "";
      showButton.disabled = true;
      closeCode();
      return;
    }
    problem.textContent = "";
    showButton.disabled = false;
    validity.textContent = validityCopy(phase.secondsLeft);
    // An errand that expired while the card was open goes off the list: every
    // verifier would refuse the code it rides in.
    const expired = errands.filter((errand) => errand.expiresAt <= now);
    if (expired.length > 0) {
      errands.splice(0, errands.length, ...errands.filter((errand) => errand.expiresAt > now));
      if (selected && expired.includes(selected)) selected = null;
      listErrands();
    }
    if (code.hidden) return;
    const payload = await buildPresentation({
      credentialBytes,
      deviceKey: pair.privateKey,
      nonce: chooseNonce(MODE_FAST),
      mode: MODE_FAST,
      timestamp: now,
      errandBytes: selected?.bytes,
    });
    const svg = (await emitQR(payload))[0];
    // The holder can close the code while it is being signed.
    if (code.hidden) return;
    generatedAt = now;
    qr.innerHTML = svg;
    await tickAge();
  };

  // Both ends of this subtraction come from `correctedNow`, so the number is
  // exact: it is one clock read twice, not two clocks compared. The spec wants
  // it on screen — `la pantalla dice en letra visible cuántos segundos tiene el
  // código` — so that the person verifying does not have to trust this app.
  const tickAge = async () => {
    if (generatedAt === null) {
      age.textContent = "";
      return;
    }
    const { now } = await correctedNow();
    age.textContent = `Generado hace ${Math.max(0, now - generatedAt)} s`;
  };

  await regenerate();
  applyLayer(nav.here().view === "card" ? nav.here().layer : null);
  const regenerateTimer = setInterval(() => void regenerate(), QR_REFRESH_MS);
  const ageTimer = setInterval(() => void tickAge(), 1000);

  return () => {
    stopFollowing();
    clearInterval(regenerateTimer);
    clearInterval(ageTimer);
    void wake?.release().catch(() => {});
    if (portraitURL) URL.revokeObjectURL(portraitURL);
  };
}
